import {
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { useCreateOrUpdateListingComparableAdjustmentMutation } from '@client/graphql/__generated__/main-operations';
import {
  ListingComparableSetFragment,
  MlsListingFragment,
} from '@client/graphql/__generated__/types';
import { MessageCirclePlusIcon, NotepadTextIcon } from 'lucide-react';
import { memo, useEffect, useState } from 'react';
import { useIsMobile } from '~/common/hooks/useIsMobile';

interface NotesTooltipProps {
  mlsListing?: MlsListingFragment;
  comparableSet?: ListingComparableSetFragment;
  isEditMode?: boolean;
  isHovered?: boolean;
}

export const NotesTooltip = memo(
  ({ mlsListing, comparableSet, isEditMode, isHovered }: NotesTooltipProps) => {
    const isMobile = useIsMobile();
    const {
      isOpen: isTooltipOpen,
      onOpen: onTooltipOpen,
      onClose: onTooltipClose,
    } = useDisclosure();
    const {
      isOpen: isEditOpen,
      onOpen: onEditOpen,
      onClose: onEditClose,
    } = useDisclosure();

    const [notes, setNotes] = useState<string | null>(null);

    const [updateComparableAdjustments, { loading }] =
      useCreateOrUpdateListingComparableAdjustmentMutation();

    useEffect(() => {
      const newNotes = comparableSet?.listingComparables?.find(
        (comparable) => comparable.mlsListing.id === mlsListing?.id
      )?.adjustments?.notes;
      setNotes(newNotes ?? null);
    }, [comparableSet?.listingComparables, mlsListing?.id]);

    const tooltipContent = notes ? notes : 'Add a comment';

    const onSave = () => {
      if (!comparableSet?.id || !mlsListing?.id) {
        return;
      }
      void (async () => {
        await updateComparableAdjustments({
          variables: {
            input: {
              listingComparableSetId: comparableSet.id,
              mlsListingId: mlsListing.id,
              adjustments: {
                notes: notes || (null as unknown as undefined),
              },
            },
          },
        });
        onEditClose();
      })();
    };

    if ((!isEditMode && !notes) || (isEditMode && !isHovered && !isMobile)) {
      return null;
    }

    return (
      <>
        <Tooltip isOpen={isTooltipOpen} label={tooltipContent}>
          <Icon
            _hover={{
              color: isEditMode ? 'cyan.700' : 'cyan.500',
            }}
            as={notes ? NotepadTextIcon : MessageCirclePlusIcon}
            boxSize={5}
            color="cyan.500"
            mr={2}
            onClick={isEditMode ? onEditOpen : onTooltipOpen}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseLeave={() => onTooltipClose()}
            onMouseOver={() => onTooltipOpen()}
          />
        </Tooltip>

        {/* NOTE: Need to add onMouseDown stopPropagation to prevent the dnd draggable under the modal from hijacking the click event */}
        <Modal
          isOpen={isEditOpen}
          returnFocusOnClose={false}
          onClose={onEditClose}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalCloseButton />
            <ModalHeader>Add a comment</ModalHeader>
            <ModalBody>
              <Textarea
                placeholder="e.g. Renovated Kitchen"
                value={notes ?? undefined}
                onChange={(e) => {
                  setNotes(e.target.value);
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </ModalBody>
            <ModalFooter gap={2}>
              <Button
                variant="outline"
                onClick={() => onEditClose()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                Cancel
              </Button>
              <Button
                isLoading={loading}
                onClick={() => {
                  onSave();
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                Save
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    );
  }
);
